import React, { useEffect, useRef, useState } from "react";
import { ZoomIn } from "lucide-react";

const SIZE = 300;

export default function AvatarCropper({ file, onCancel, onConfirm }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const dragRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { imgRef.current = img; setOffset({ x: 0, y: 0 }); setZoom(1); setLoaded(true); };
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const baseScale = () => {
    const img = imgRef.current;
    return Math.max(SIZE / img.naturalWidth, SIZE / img.naturalHeight);
  };
  const clamp = (off, s) => {
    const img = imgRef.current;
    const w = img.naturalWidth * s, h = img.naturalHeight * s;
    const mx = Math.max(0, (w - SIZE) / 2), my = Math.max(0, (h - SIZE) / 2);
    return { x: Math.max(-mx, Math.min(mx, off.x)), y: Math.max(-my, Math.min(my, off.y)) };
  };

  useEffect(() => {
    if (!loaded) return;
    const c = canvasRef.current, ctx = c.getContext("2d"), img = imgRef.current;
    const s = baseScale() * zoom, w = img.naturalWidth * s, h = img.naturalHeight * s;
    ctx.fillStyle = "#0a0a0a"; ctx.fillRect(0, 0, SIZE, SIZE);
    ctx.drawImage(img, (SIZE - w) / 2 + offset.x, (SIZE - h) / 2 + offset.y, w, h);
  }, [zoom, offset, loaded]);

  const onDown = (e) => { const p = e.touches ? e.touches[0] : e; dragRef.current = { x: p.clientX, y: p.clientY, o: offset }; };
  const onMove = (e) => {
    if (!dragRef.current) return;
    const p = e.touches ? e.touches[0] : e;
    const s = baseScale() * zoom;
    setOffset(clamp({ x: dragRef.current.o.x + (p.clientX - dragRef.current.x), y: dragRef.current.o.y + (p.clientY - dragRef.current.y) }, s));
  };
  const onUp = () => { dragRef.current = null; };

  const changeZoom = (z) => { setZoom(z); setOffset((o) => clamp(o, baseScale() * z)); };

  const confirm = () => {
    setSaving(true);
    const OUT = 512, img = imgRef.current, k = OUT / SIZE;
    const out = document.createElement("canvas"); out.width = OUT; out.height = OUT;
    const octx = out.getContext("2d");
    const s = baseScale() * zoom * k, w = img.naturalWidth * s, h = img.naturalHeight * s;
    octx.fillStyle = "#0a0a0a"; octx.fillRect(0, 0, OUT, OUT);
    octx.drawImage(img, (OUT - w) / 2 + offset.x * k, (OUT - h) / 2 + offset.y * k, w, h);
    out.toBlob((b) => onConfirm(b), "image/jpeg", 0.9);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={onCancel} data-testid="avatar-cropper">
      <div className="rsp-card p-5 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
        <div className="font-head uppercase tracking-wide text-lg mb-3">Adjust Photo</div>
        <div className="relative mx-auto" style={{ width: SIZE, height: SIZE }}>
          <canvas ref={canvasRef} width={SIZE} height={SIZE}
            className="rounded-lg touch-none cursor-move select-none"
            onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
            onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp} data-testid="cropper-canvas" />
          <div className="pointer-events-none absolute inset-0 rounded-lg" style={{ boxShadow: "0 0 0 9999px rgba(0,0,0,0)", outline: "2px dashed rgba(240,196,25,0.6)", outlineOffset: "-2px", borderRadius: "9999px" }} />
        </div>
        <div className="flex items-center gap-2 mt-4">
          <ZoomIn size={16} className="gold" />
          <input type="range" min="1" max="3" step="0.02" value={zoom} onChange={(e) => changeZoom(parseFloat(e.target.value))}
            className="flex-1 accent-[var(--rsp-gold)]" data-testid="cropper-zoom" />
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={onCancel} className="flex-1 py-2.5 rounded-lg border border-[var(--rsp-border)] text-[var(--rsp-gray)] hover:text-[var(--rsp-cream)]" data-testid="cropper-cancel">Cancel</button>
          <button onClick={confirm} disabled={!loaded || saving} className="btn-gold flex-1 py-2.5" data-testid="cropper-save">{saving ? "Saving…" : "Save Photo"}</button>
        </div>
      </div>
    </div>
  );
}
